import { cn } from '@wellesley/ui'
import { AppsLink } from '@wellesley/router'
import { UserAvatar } from '@wellesley/user/components/UserAvatar'
import { useSelector } from 'react-redux'
import { emojisSlice } from 'components/emojis/emojisSlice'
import addEmojisToText from 'helpers/addEmojisToText'
import { getGroupChannelPostPath } from '@wellesley/group'
import { getPostRoute, getUserRoute } from 'helpers/routes'
import { FC, useEffect, useRef, useState } from 'react'

import getAvatar from './helpers/getAvatar'
import { THeader } from './types'
import OriginalPoster from './OriginalPoster'
import HeaderControls from './HeaderControls'
import { getSubtitle } from './helpers/getSubtitle'
import isBotProfile from 'helpers/ai/isBotProfile'
import BotBadge from 'components/BotBadge'

const Header: FC<THeader> = (props) => {
  const {
    feed,
    title = feed.author.displayName,
    subtitle = getSubtitle(feed.author),
    isOriginalPoster,
    showOriginalPoster = true,
    showPostOwnerImage,
    renderUserWrapper,
    canShowControls = !feed.deleted,
    groupName,
    channel,
  } = props

  const author = feed.author
  const emojis = useSelector(emojisSlice.selectors.selectEmojis)

  const canShowPostOwnerBadge = showOriginalPoster && isOriginalPoster

  const controlsContainerRef = useRef<HTMLDivElement | null>(null)
  const [controlsWidth, setControlWidth] = useState(50)

  useEffect(() => {
    if (!controlsContainerRef) return

    const containerWidth = controlsContainerRef.current?.clientWidth

    containerWidth && setControlWidth(containerWidth)
  }, [controlsContainerRef.current, feed.edited])

  const postRoute =
    groupName && channel && author.id === channel.id
      ? getGroupChannelPostPath({
          groupName,
          channel,
          postId: feed.id,
        })
      : getPostRoute(author, feed.id)

  const {
    mainAvatar,
    secondaryAvatar,
    isSecondaryAvatar,
    mainImageName,
    secondaryImageName,
  } = getAvatar(feed, showPostOwnerImage)

  const isBot = isBotProfile(author)

  const content = renderUserWrapper?.({
    user: author,
    el: (
      <div
        className="flex cursor-pointer items-center gap-2"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        <AppsLink
          to={getUserRoute(author)}
          data-testid="header-user-link"
          className={cn(
            'flex w-full',
            isSecondaryAvatar ? 'gap-xs' : 'gap-xxs',
          )}
        >
          <div className="relative z-[1]">
            <UserAvatar
              name={addEmojisToText(mainImageName, emojis)}
              imgUrl={mainAvatar}
            />
            {isSecondaryAvatar && (
              <div className="absolute -bottom-[4px] -right-[6px] rounded-full border-2 border-white">
                <UserAvatar
                  size={20}
                  name={addEmojisToText(secondaryImageName, emojis)}
                  imgUrl={secondaryAvatar}
                />
              </div>
            )}
          </div>
          <div className="shrink-1 line-clamp-1 flex flex-col justify-center text-sm text-gray-900">
            <div className="gap-xxxs flex items-center">
              <span
                data-testid="header-title"
                className="inline-block truncate font-semibold leading-4"
                dangerouslySetInnerHTML={{
                  __html: addEmojisToText(title || '', emojis),
                }}
              />
              {canShowPostOwnerBadge && <OriginalPoster />}
              {isBot && <BotBadge />}
            </div>
            <span
              data-testid="header-subtitle"
              className="mt-[1px] inline-block truncate font-medium leading-4 text-gray-600"
              dangerouslySetInnerHTML={{
                __html: subtitle || '',
              }}
            />
          </div>
        </AppsLink>
      </div>
    ),
  })

  return (
    <div className="flex cursor-pointer justify-between">
      <div
        style={{
          maxWidth: `calc(100% - ${controlsWidth}px)`,
        }}
      >
        <AppsLink to={postRoute}>{content}</AppsLink>
      </div>
      {canShowControls && (
        <div ref={controlsContainerRef}>
          <HeaderControls {...props} />
        </div>
      )}
    </div>
  )
}

export { Header }
